/**
 * Syncs URL to AppContext:
 * 1. /app/property/:propertyId → selectProperty + setPropertyScope
 * 2. /app/fleet or other fleet-level routes → setFleetScope
 * Parses propertyId from pathname since layout may not receive child route params.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';

export function RouteSync() {
  const location = useLocation();
  const { selectProperty, properties, setFleetScope, setPropertyScope, navigationScope } = useApp();

  const propertyMatch = location.pathname.match(/^\/app\/property\/([^/]+)/);
  const propertyId = propertyMatch?.[1] ?? null;

  // Also check for ?property= query param (scope-driven analytics, alerts, etc.)
  const searchParams = new URLSearchParams(location.search);
  const queryPropertyId = searchParams.get('property');

  const effectivePropertyId = propertyId ?? queryPropertyId;

  useEffect(() => {
    if (effectivePropertyId) {
      const property = properties.find((p) => p.id === effectivePropertyId);
      if (property) {
        selectProperty(effectivePropertyId);
        // Only update scope if it doesn't match (avoid unnecessary re-renders)
        if (navigationScope.type !== 'property' || navigationScope.propertyId !== effectivePropertyId) {
          setPropertyScope(effectivePropertyId, property.name);
        }
      }
    } else {
      // Fleet-level routes: /app/fleet, /app/properties, /app/analytics (no ?property), etc.
      const isFleetRoute =
        location.pathname === '/app/fleet' ||
        location.pathname === '/app/properties' ||
        location.pathname === '/app' ||
        location.pathname === '/app/';
      if (isFleetRoute && navigationScope.type !== 'fleet') {
        setFleetScope();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: sync when URL or properties change
  }, [effectivePropertyId, location.pathname, properties]);

  return null;
}
